import { V2 } from '@eventr/common'
import { ColoursObj, Icon, TEventrIcons } from '@eventr/common-storybook'
import { Box } from '@mui/material'
import { useMemo } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { useShallow } from 'zustand/react/shallow'
import { BreakoutSection } from '../../common/breakoutSection'
import { DisclosureGroup } from '../../common/DisclosureGroup'
import { useGlobalRouter } from '../../feat/globalRouter/useGlobalRouter'
import i18n from '../../i18n/i18next'
import { Paths } from '../../routes'
import { useBoundStore } from '../../state/bound.store'
import { homeTabSignal } from '../../state/itineraryState/itinerarySignals'
import { usePastPresentElements } from '../hooks/usePastPresentElements'
import { useScreenTypeContext } from '../state/screenTypeProvider'
import { useSectionV2Context } from '../state/sectionProvider'
import { publicColour } from '../v2Utils'
import { ElementRenderer } from './elementRender'
import { ScreenType } from './rendererLocator'
interface IPastSorterProps {
    data: V2
    screenType: ScreenType
    id?: string
}

export const PastSorter = ({ data, screenType, id }: IPastSorterProps) => {
    const { pushPath } = useGlobalRouter()
    const { isPublic } = useSectionV2Context()
    const { openDisclosure, stickyHeaders } = useScreenTypeContext()
    const { past } = useBoundStore(
        useShallow((s) => {
            return {
                past: s.past,
            }
        })
    )

    const { pastElements, presentElements } = usePastPresentElements(
        data,
        screenType
    )

    const elements = useMemo(() => {
        if (past) {
            return pastElements
        }
        return presentElements
    }, [past, pastElements, presentElements])

    const isPartOfAny = !past
        ? presentElements.length > 0
        : pastElements.length > 0

    const headerIcon =
        data.iconOverwrite ?? data.rendererOptions?.settings?.headerIcon

    const renderComponents = (d: any, past?: boolean) => {
        let components: any[] = data.rendererOptions?.components

        return (
            <ElementRenderer
                d={d}
                components={components}
                screenType={screenType}
                past={past}
            />
        )
    }

    const onBreakoutSectionClick = () => {
        if (data.category) {
            pushPath(
                Paths.itemList(data.sectionID, data.category) +
                    `?screen=${screenType}${
                        data.ogCode ? '&ogCode=' + data.ogCode : ''
                    }`,
                {
                    animation: 'left',
                }
            )
            return
        }
        pushPath(
            Paths.itemList(data.sectionID) +
                `?screen=${screenType}${
                    data.ogCode ? '&ogCode=' + data.ogCode : ''
                }`,
            {
                animation: 'left',
            }
        )
    }

    const personalScreen = () => {
        if (data.usePage || data.sectionID === 8) {
            return (
                <BreakoutSection
                    title={
                        data.sectionNameOverwrite ??
                        i18n.t(`sections.${data.sectionName.toLowerCase()}`, {
                            count: 0,
                        })
                    }
                    onClick={onBreakoutSectionClick}
                    colour={isPublic ? publicColour : undefined}
                    borderColour={isPublic ? publicColour : undefined}
                    icon={(headerIcon as TEventrIcons) ?? 'Activity'}
                    count={elements.length}
                    iconColour={data.colour}
                />
            )
        }
        return (
            <DisclosureGroup
                stickyHeader
                id={id}
                childrenHandlePadding
                containerStyle={{
                    backgroundColor: ColoursObj.backgrounds.primary,
                }}
                title={
                    data.sectionNameOverwrite ??
                    i18n.t(`sections.${data.sectionName.toLowerCase()}`, {
                        count: 0,
                    })
                }
                icon={
                    <Icon
                        name={(headerIcon as TEventrIcons) ?? 'Activity'}
                        iconColour={ColoursObj.backgrounds.primary}
                        bubbleColour={isPublic ? publicColour : undefined}
                        bubble
                    />
                }
                count={elements.length}
                content={elements.map((d, i) => {
                    return (
                        <Box padding={'0 8px'} key={i}>
                            {renderComponents(d, past)}
                        </Box>
                    )
                })}
            />
        )
    }

    const teamScreen = () => {
        const count = pastElements.length + presentElements.length
        if (pastElements.length + presentElements.length === 0) return null
        if (data.usePage && !openDisclosure) {
            return (
                <BreakoutSection
                    title={
                        data.sectionNameOverwrite ??
                        i18n.t(`sections.${data.sectionName.toLowerCase()}`, {
                            count: 0,
                        })
                    }
                    colour={isPublic ? publicColour : undefined}
                    onClick={onBreakoutSectionClick}
                    icon={(headerIcon as TEventrIcons) ?? 'Activity'}
                    count={count}
                    iconColour={data.colour}
                />
            )
        }

        return (
            <DisclosureGroup
                stickyHeader={stickyHeaders}
                childrenHandlePadding
                id={id}
                containerStyle={{
                    backgroundColor: ColoursObj.backgrounds.primary,
                }}
                title={
                    data.sectionNameOverwrite ??
                    i18n.t(`sections.${data.sectionName.toLowerCase()}`, {
                        count: 0,
                    })
                }
                icon={
                    <Icon
                        name={headerIcon as TEventrIcons}
                        iconColour={ColoursObj.backgrounds.primary}
                        bubbleColour={isPublic ? publicColour : undefined}
                        bubble
                    />
                }
                isOpen={openDisclosure}
                count={count}
                content={
                    count > 10 ? (
                        <div>
                            <Virtuoso
                                style={{ height: '600px' }}
                                data={[...presentElements, ...pastElements]}
                                itemContent={(index, itemData) => {
                                    const isPastElement =
                                        index >= presentElements.length
                                    return (
                                        <Box padding={'0 8px'}>
                                            {renderComponents(
                                                itemData,
                                                isPastElement
                                            )}
                                        </Box>
                                    )
                                }}
                            />
                        </div>
                    ) : (
                        <div>
                            {presentElements.map((d, i) => {
                                return (
                                    <Box padding={'0 8px'} key={i}>
                                        {renderComponents(d)}
                                    </Box>
                                )
                            })}
                            {pastElements.length > 0 && (
                                <Box padding={'0 8px'}>
                                    {pastElements.map((d, i) => {
                                        return (
                                            <Box key={i}>
                                                {renderComponents(d, true)}
                                            </Box>
                                        )
                                    })}
                                </Box>
                            )}
                        </div>
                    )
                }
            />
        )
    }

    const teamMemberScreen = () => {
        return (
            <DisclosureGroup
                stickyHeader
                containerStyle={{
                    backgroundColor: ColoursObj.backgrounds.primary,
                }}
                title={
                    data.sectionNameOverwrite ??
                    i18n.t(`sections.${data.sectionName.toLowerCase()}`, {
                        count: 0,
                    })
                }
                icon={
                    <Icon
                        name={(headerIcon as TEventrIcons) ?? 'Activity'}
                        iconColour={ColoursObj.backgrounds.primary}
                        bubbleColour={isPublic ? publicColour : undefined}
                        bubble
                    />
                }
                count={
                    homeTabSignal.value === 'itinerary'
                        ? past
                            ? pastElements.length
                            : presentElements.length
                        : pastElements.length + presentElements.length
                }
                content={
                    <>
                        {presentElements.map((d, i) => {
                            return <Box key={i}>{renderComponents(d)}</Box>
                        })}
                        {pastElements.map((d, i) => {
                            return (
                                <Box key={i}>{renderComponents(d, true)}</Box>
                            )
                        })}
                    </>
                }
            />
        )
    }

    return (
        <>
            {screenType === 'personal' && isPartOfAny && personalScreen()}
            {screenType === 'teamMember' && isPartOfAny && teamMemberScreen()}
            {screenType === 'team' && teamScreen()}
            {screenType === 'edit' && (
                <>
                    {presentElements.map((d, i) => {
                        return <Box key={i}> {renderComponents(d)}</Box>
                    })}
                    {pastElements.map((d, i) => {
                        return <Box key={i}> {renderComponents(d, true)}</Box>
                    })}
                </>
            )}
        </>
    )
}
